import { CheckCircle2, Eye, EyeOff, Pencil, RefreshCw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  type Account,
  useCheckAccountProxy,
  useDeleteAccount,
  useRenameAccount,
  useToggleAccount,
  useUpdateAccount,
} from "@/api/accounts";
import { Button, Input, Sheet } from "@/design";
import { haptic } from "@/lib/telegram";
import {
  isProxyFormValid,
  ProxySettingsForm,
  proxyFormFromAccount,
  proxyFormKey,
  proxyInputFromForm,
  type ProxyFormState,
} from "./ProxySettingsForm";

interface Props {
  account: Account | null;
  open: boolean;
  onClose: () => void;
}

export function AccountEditSheet({ account, open, onClose }: Props) {
  const [name, setName] = useState("");
  const [proxy, setProxy] = useState<ProxyFormState>(() => proxyFormFromAccount(null));
  const rename = useRenameAccount();
  const toggle = useToggleAccount();
  const update = useUpdateAccount();
  const checkProxy = useCheckAccountProxy();
  const remove = useDeleteAccount();

  useEffect(() => {
    if (open && account) {
      setName(account.name);
      setProxy(proxyFormFromAccount(account));
    }
  }, [open, account]);

  if (!account) return null;

  const nameChanged = Boolean(name.trim() && name.trim() !== account.name);
  const initialProxy = proxyFormFromAccount(account);
  const proxyChanged =
    proxyFormKey(proxy) !== proxyFormKey(initialProxy) || Boolean(proxy.enabled && proxy.password.trim());
  const proxyValid = isProxyFormValid(proxy);

  const saveName = async () => {
    if (!nameChanged) return;
    haptic.impact("medium");
    try {
      await rename.mutateAsync({ id: account.id, name: name.trim() });
      haptic.notify("success");
      toast.success("Название обновлено");
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось переименовать");
    }
  };

  const saveProxy = async () => {
    if (!proxyChanged || !proxyValid) return;
    haptic.impact("medium");
    try {
      const updated = await update.mutateAsync({
        id: account.id,
        body: { proxy: proxy.enabled ? proxyInputFromForm(proxy) : null },
      });
      setProxy(proxyFormFromAccount(updated));
      haptic.notify("success");
      toast.success(proxy.enabled ? "Прокси сохранён и проверен" : "Прокси отключён");
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось сохранить прокси");
    }
  };

  const runProxyCheck = async () => {
    haptic.impact("light");
    try {
      const resp = await checkProxy.mutateAsync(account.id);
      setProxy(proxyFormFromAccount(resp.account));
      haptic.notify("success");
      toast.success("Прокси работает");
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Прокси недоступен");
    }
  };

  const toggleEnabled = async () => {
    haptic.impact("medium");
    try {
      await toggle.mutateAsync({ id: account.id, enabled: !account.is_enabled });
      haptic.notify("success");
      toast.success(account.is_enabled ? "Аккаунт поставлен на паузу" : "Аккаунт включён");
      onClose();
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось изменить статус");
    }
  };

  const deleteAccount = async () => {
    if (!confirm(`Удалить аккаунт ${account.phone}?`)) return;
    haptic.impact("heavy");
    try {
      await remove.mutateAsync(account.id);
      haptic.notify("success");
      toast.success("Аккаунт удалён");
      onClose();
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не удалось удалить аккаунт");
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Аккаунт" description={account.phone} size="full">
      <div className="space-y-5">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="text-[11px] uppercase tracking-wider text-text-dim font-medium">Статус</div>
          <div className="mt-1 text-lg font-semibold">{account.name}</div>
          <div className="mt-1 text-sm text-text-muted">
            {account.is_enabled ? "Участвует в рассылках" : "На паузе"}
          </div>
          {account.proxy && (
            <div className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-lg bg-white/[0.04] px-2 py-1 text-xs text-text-muted">
              <CheckCircle2 className="size-3.5 shrink-0 text-success" />
              <span className="truncate">
                {account.proxy.proxy_type.toUpperCase()} {account.proxy.host}:{account.proxy.port}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Input
            label="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            leftIcon={<Pencil />}
            maxLength={128}
          />
          <Button
            fullWidth
            onClick={saveName}
            disabled={!nameChanged || rename.isPending}
            loading={rename.isPending}
          >
            Сохранить название
          </Button>
        </div>

        <div className="space-y-3">
          <ProxySettingsForm
            value={proxy}
            onChange={setProxy}
            current={account.proxy}
            disabled={update.isPending || checkProxy.isPending}
          />
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="secondary"
              leftIcon={<Save />}
              onClick={saveProxy}
              disabled={!proxyChanged || !proxyValid || update.isPending}
              loading={update.isPending}
            >
              Сохранить
            </Button>
            <Button
              variant="ghost"
              leftIcon={<RefreshCw />}
              onClick={runProxyCheck}
              disabled={!account.proxy || checkProxy.isPending}
              loading={checkProxy.isPending}
            >
              Проверить
            </Button>
          </div>
          {account.proxy?.last_failed_at && !account.proxy.is_alive && (
            <div className="rounded-xl border border-danger/20 bg-danger/10 px-3 py-2 text-xs leading-relaxed text-danger">
              Аккаунт автоматически ставится на паузу, если proxy падает во время рассылок или реакций.
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <Button
            variant="secondary"
            leftIcon={account.is_enabled ? <EyeOff /> : <Eye />}
            onClick={toggleEnabled}
            loading={toggle.isPending}
          >
            {account.is_enabled ? "Пауза" : "Включить"}
          </Button>
          <Button variant="ghost" leftIcon={<Trash2 />} onClick={deleteAccount} loading={remove.isPending}>
            Удалить
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
